import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import type { Lead } from '../../../types/leads';

interface DragState {
  draggingLead: Lead | null;
  sourceStageId: string | null;
  hoverStageId: string | null;
}

interface DragContextType extends DragState {
  startDrag: (lead: Lead, stageId: string) => void;
  setHoverStage: (stageId: string | null) => void;
  endDrag: () => void;
  dropOnStage: (stageId: string) => void;
  onDrop?: (leadId: string, fromStageId: string, toStageId: string) => void;
}

const DragContext = createContext<DragContextType | null>(null);

interface DragProviderProps {
  children: ReactNode;
  onDrop?: (leadId: string, fromStageId: string, toStageId: string) => void;
}

export function DragProvider({ children, onDrop }: Readonly<DragProviderProps>) {
  const [state, setState] = useState<DragState>({
    draggingLead: null,
    sourceStageId: null,
    hoverStageId: null,
  });

  const startDrag = useCallback((lead: Lead, stageId: string) => {
    setState({ draggingLead: lead, sourceStageId: stageId, hoverStageId: null });
  }, []);

  const setHoverStage = useCallback((stageId: string | null) => {
    setState((prev) => ({ ...prev, hoverStageId: stageId }));
  }, []);

  const endDrag = useCallback(() => {
    setState({ draggingLead: null, sourceStageId: null, hoverStageId: null });
  }, []);

  const dropOnStage = useCallback(
    (targetStageId: string) => {
      if (state.draggingLead && state.sourceStageId && state.sourceStageId !== targetStageId) {
        onDrop?.(state.draggingLead.id, state.sourceStageId, targetStageId);
      }
      setState({ draggingLead: null, sourceStageId: null, hoverStageId: null });
    },
    [state.draggingLead, state.sourceStageId, onDrop],
  );

  const contextValue = useMemo(
    () => ({ ...state, startDrag, setHoverStage, endDrag, dropOnStage, onDrop }),
    [state, startDrag, setHoverStage, endDrag, dropOnStage, onDrop],
  );

  return <DragContext.Provider value={contextValue}>{children}</DragContext.Provider>;
}

export function useDragContext() {
  const ctx = useContext(DragContext);
  if (!ctx) throw new Error('useDragContext must be used within DragProvider');
  return ctx;
}
